import React, { useEffect } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { fetchData } from 'reducers/dataTable';
import { openModal } from 'reducers/modals';
// components
import { FormattedMessage } from 'react-intl';
import DataTable from 'components/DataTable/DataTable';
import { CustomColumn } from 'components/DataTable/Columns';
import NothingBox from 'components/NothingBox';
import OpenBinModal from './OpenBinModal';
import CloseBinModal from './CloseBinModal';
import BinHistoryModal from './BinHistoryModal';
// utils
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { formatNumber } from 'utils';
import { formatDateForUser } from 'utils/regionalDateHelper';
import { lbsToDisplay, weightUnit } from '../utils';
// selectors
import { getCurrentCountryCode } from 'selectors/regionalDateSelectors';
// hooks
import useDataTableController from 'hooks/useDataTableController';

const labels = {
  bin: <FormattedMessage id="component.dataTable.headers.binID" />,
  inventory: <FormattedMessage id="component.feedDeliveries.currentInventory" />,
  group: <FormattedMessage id="general.group" />,
  openDate: <FormattedMessage id="component.feedDeliveries.binOpenDate" />,
  closeDate: <FormattedMessage id="component.feedDeliveries.binCloseDate" />,
};

const BinsTab = ({
  farmId,
  measurementSystem,
  countryCode,
  fetchData,
  isLoading,
  meta,
  resources,
  reqParams,
  openModal,
}) => {
  const { onPageChange, onPerPageChange, onSortChange } = useDataTableController(fetchData, reqParams);
  const { sort, page, per_page } = reqParams;

  const paginationProps = {
    totalItems: meta.total,
    currentPage: page,
    perPage: per_page,
    onPageChange,
    onPerPageChange,
  };

  const loadData = () => {
    fetchData(reqParams).catch(toastResponseErrors);
  };

  useEffect(() => {
    loadData();
  }, []);

  const openOpenModal = (bin) => {
    openModal(
      <OpenBinModal farmId={farmId} bin={bin} onSuccess={loadData} />
    );
  };

  const openCloseModal = (bin) => {
    openModal(
      <CloseBinModal farmId={farmId} bin={bin} measurementSystem={measurementSystem} onSuccess={loadData} />
    );
  };

  // Corrections to open/close history happen there, which resets the bin state on this list
  const openHistoryModal = (bin) => {
    openModal(
      <BinHistoryModal farmId={farmId} bin={bin} measurementSystem={measurementSystem} onChanged={loadData} />
    );
  };

  const renderBinColumn = (bin) => (
    <CustomColumn label={labels.bin}>
      <span className="color-primary bin-link" onClick={() => openHistoryModal(bin)}>{bin.name}</span>
    </CustomColumn>
  );

  const renderInventoryColumn = ({ running_inventory }) => (
    <CustomColumn label={labels.inventory}>
      {formatNumber(lbsToDisplay(running_inventory, measurementSystem))} {weightUnit(measurementSystem)}
    </CustomColumn>
  );

  renderInventoryColumn.propTypes = { running_inventory: T.number };

  const renderGroupColumn = ({ current_cycle }) => (
    <CustomColumn label={labels.group}>
      {current_cycle ? current_cycle.pig_group_name : <FormattedMessage id="general.notAvail" />}
    </CustomColumn>
  );

  renderGroupColumn.propTypes = { current_cycle: T.object };

  // Once an open date is entered it stays locked/visible until the cycle is closed
  const renderOpenDateColumn = (bin) => (
    <CustomColumn label={labels.openDate}>
      {bin.current_cycle ? (
        <span className="locked-date">
          <i className="fa fa-lock mr-5" />
          {formatDateForUser(bin.current_cycle.opened_on, countryCode)}
        </span>
      ) : (
        <button className="button small light-gray" onClick={() => openOpenModal(bin)}>
          <FormattedMessage id="component.feedDeliveries.openBin" />
        </button>
      )}
    </CustomColumn>
  );

  const renderCloseDateColumn = (bin) => (
    <CustomColumn label={labels.closeDate}>
      {bin.current_cycle ? (
        <button className="button small primary" onClick={() => openCloseModal(bin)}>
          <FormattedMessage id="component.feedDeliveries.closeBin" />
        </button>
      ) : (
        <FormattedMessage id="general.notAvail" />
      )}
    </CustomColumn>
  );

  const columns = [
    { label: labels.bin, flex: '1 0 120px', renderer: renderBinColumn, sortKey: 'name' },
    { label: labels.inventory, flex: '1 0 120px', renderer: renderInventoryColumn },
    { label: labels.group, flex: '1 0 130px', renderer: renderGroupColumn },
    { label: labels.openDate, flex: '1 0 140px', renderer: renderOpenDateColumn },
    { label: labels.closeDate, flex: '1 0 140px', renderer: renderCloseDateColumn },
  ];

  return (
    <div className="bins-tab">
      <DataTable
        data={resources}
        columns={columns}
        sort={sort}
        onSortChange={onSortChange}
        isLoading={isLoading}
        paginationProps={paginationProps}
      />

      <NothingBox display={!isLoading && resources.length === 0}>
        <FormattedMessage tagName="h1" id="component.nothingBox.farmHasNoBins" />
      </NothingBox>
    </div>
  );
};

BinsTab.propTypes = {
  farmId: T.number.isRequired,
  measurementSystem: T.oneOf(['imperial', 'metric']),
  countryCode: T.string,
  fetchData: T.func.isRequired,
  isLoading: T.bool.isRequired,
  meta: T.object,
  resources: T.array.isRequired,
  reqParams: T.object.isRequired,
  openModal: T.func.isRequired,
};

export default connect(
  (state) => ({
    resources: state.dataTable.feedDeliveryBins.resources,
    reqParams: state.dataTable.feedDeliveryBins.params,
    isLoading: state.dataTable.feedDeliveryBins.isLoading,
    meta: state.dataTable.feedDeliveryBins.meta,
    countryCode: getCurrentCountryCode(state),
  }), (dispatch, { farmId }) => ({
    fetchData: (query) => dispatch(fetchData(`/feed_deliveries/farms/${farmId}/bins`, 'feedDeliveryBins', query)),
    openModal: (modal) => dispatch(openModal(modal)),
  }),
)(BinsTab);
